import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Empty, Form, Input, message, Select, Statistic } from 'antd'
import { ArrowLeftOutlined, SelectOutlined, Loading3QuartersOutlined, SwapRightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { BackButton, BasicSettingWrapper, CustomSettingWrapper, FormButton, RoundedTag, SettingWrapper, SubTitle, UnderlineBoldTxt } from './targeting.styled'
import { fetchSegment } from '../../apis/segment'
import dayjs from 'dayjs'
import MainTable from '../../components/Basic/MainTable'
import useParams from '../../hook/useParams'
import { useSelector } from 'react-redux'
import TargetConfirmModal from './Component/ConfirmationModal'
import SegmentDetail from '../Segment/Segment/SegmentDetail'
import { fetchExportByBrand } from '../../apis/export'
import { fetchInvoiceSegment } from '../../apis/invoice'
import { useGoogleLogin } from '@react-oauth/google'
import { TRANS_TYPE } from '../../constants/TransType'
import { getGoogleRefreshToken } from '../../apis/auth'

const allowedToSelect = ['done']
function TargetForm (props) {
  const { goBackPath, addMode } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { goBackPath: goBackFromLocation } = location?.state || {}
  const user = useSelector((state) => state.user)
  const { nowBrand, nowGroup } = user || {}
  const params = useParams({ brandId: nowBrand, status: 1, maxResults: 5 }, false)
  const { fetchParams, selectedItem, handleTableParams, setReady } = params
  const paramsInvoice = useParams({ brandId: nowBrand, maxResults: 5 }, false)
  const { fetchParams: fetchParamsInv, selectedItem: selectedItemInv, handleTableParams: handleTableParamsInv, setReady: setReadyInv } = paramsInvoice
  const [selectedSegment, setSelectedSegment] = useState()
  const [showModal, setShowModal] = useState(false)
  const [confirmData, setConfirmData] = useState({})
  const [dataExport, setDataExport] = useState([])
  const selectedDataExport = Form.useWatch('dataExportId', form)
  const selectedExportObj = dataExport?.find(i => i.value === selectedDataExport)
  const invoiceSegmentDisabled = selectedExportObj?.mappingKeyName !== 'hashedphone' && selectedExportObj?.mappingKeyName !== 'adid'

  const login = useGoogleLogin({
    onSuccess: async auth => {
      const res = await getGoogleRefreshToken(auth?.code)
      if (!res?.status === 200) message.error(res?.data)
      setConfirmData({ ...form.getFieldsValue(), refreshToken: res?.data?.refresh_token })
      setShowModal(true)
    },
    onError: err => message.error(`Login Failed: ${err}`),
    scope: 'https://www.googleapis.com/auth/adwords',
    flow: 'auth-code'
  })

  const goBack = () => navigate(goBackFromLocation || goBackPath)

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`target.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({
    name, label: ln_t(`target.${name}`), rules: [{ required, message: defPlaceholder(name, type) }]
  })

  const handleSave = val => {
    if (selectedItem?.length && selectedItemInv?.length) return message.error(ln_t('message.error.chooseOneAudience'))
    if (selectedExportObj?.transType === TRANS_TYPE.GOOGLE_ADS) return login()
    setConfirmData(val)
    setShowModal(true)
  }

  const apiFunc = async () => fetchSegment({ ...fetchParams, previewStatus: 'done' })
  const segmentDisable = record => !record.previewCount || !allowedToSelect.includes(record.previewStatus)
  const columns = [
    {
      title: ln_t('segment.name'),
      dataIndex: 'name',
      render: (e, data) => <>
        {e} {segmentDisable(data)
          ? ''
          : <SelectOutlined
              onClick={() => setSelectedSegment(data)}
              style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--primary)', marginLeft: '6px' }}
            />} </>
    },
    {
      title: ln_t('segment.quantity'),
      dataIndex: 'previewCount',
      render: (e, data) => data?.previewStatus === 'done'
        ? <Statistic value={e} />
        : <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}><Loading3QuartersOutlined spin /></div>
    }
  ]
  const apiFuncInvoice = async () => fetchInvoiceSegment({ ...fetchParamsInv, status: 'done' })
  const segmentInvDisable = record => !record.count || !allowedToSelect.includes(record.status)
  const columnsInv = [
    {
      title: ln_t('segment.name'),
      dataIndex: 'name',
      render: (e, data) => <>
        {e} {segmentInvDisable(data)
          ? ''
          : <SelectOutlined
              onClick={() => setSelectedSegment({ ...data, isInvoice: true })}
              style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--primary)', marginLeft: '6px' }}
            />} </>
    },
    {
      title: ln_t('segment.quantity'),
      dataIndex: 'count',
      render: (e, data) => data?.status === 'done'
        ? <Statistic value={e} />
        : <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}><Loading3QuartersOutlined spin /></div>
    }
  ]
  const getDataExport = async () => {
    const exp = await fetchExportByBrand(nowGroup, nowBrand, { maxResults: 999, status: 1 })
    if (exp?.status === 200) setDataExport(exp.data.map(({ id, name, ...other }) => ({ value: id, label: name, ...other })))
  }

  useEffect(() => {
    handleTableParams('add', { pageSize: 5 })
    handleTableParamsInv('add', { pageSize: 5 })
    getDataExport()
    setReady(true)
    setReadyInv(true)
  }, [])
  useEffect(() => { form.setFieldValue('segments', selectedItem) }, [selectedItem])
  useEffect(() => { form.setFieldValue('segmentsInvoice', selectedItemInv) }, [selectedItemInv])

  const formatDate = date => dayjs(date).format('DD-MM-YYYY HH:mm:ss')
  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={goBack} />
        {ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} {ln_t('target.targeting')}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)}>
          <SettingWrapper>
            <BasicSettingWrapper reverse>
              <SubTitle>{ln_t('util.basicSetting')}</SubTitle>
              <Form.Item {...defConfig('name', 'input')}>
                  <Input placeholder={defPlaceholder('name', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('dataExport', 'select')} name="dataExportId">
                <Select
                  showSearch
                  placeholder={defPlaceholder('dataExport', 'select')}
                  options={dataExport}
                  optionFilterProp="children"
                />
              </Form.Item>
              <Form.Item {...defConfig('segments', 'select', !selectedItemInv?.length)}>
                <MainTable
                  columns={columns}
                  rowKey="id"
                  apiFunc={apiFunc}
                  params={params}
                  useSelection={true}
                  disabledRow={segmentDisable}
                  />
              </Form.Item>
              <Form.Item {...defConfig('segmentsInvoice', 'select', !selectedItem?.length)}>
                <MainTable
                  columns={columnsInv}
                  rowKey="id"
                  apiFunc={apiFuncInvoice}
                  params={paramsInvoice}
                  useSelection={true}
                  disabledRow={record => segmentInvDisable(record) || invoiceSegmentDisabled}
                />
              </Form.Item>
              <FormButton>
                <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
                <Button type="primary" htmlType="submit">{ln_t('util.confirm')}</Button>
              </FormButton>
            </BasicSettingWrapper>
            <CustomSettingWrapper reverse>
              <SubTitle>{ln_t('segment.segment')} {ln_t('target.detail')}</SubTitle>
              {selectedSegment
                ? <>
                    <div style={{ fontWeight: 500 }}>{selectedSegment?.name}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(0,0,0,.35)' }}>{ln_t('util.updatedAt')}: {formatDate(selectedSegment?.updatedAt)}</div>
                    <RoundedTag><Statistic value={selectedSegment?.previewCount || selectedSegment?.count || 0} /> {ln_t('target.users')}</RoundedTag>
                    <div className="mt-4">
                      <UnderlineBoldTxt>{ln_t('util.setting')}</UnderlineBoldTxt>
                      {selectedSegment?.isInvoice
                        ? <div>{ln_t('invoice.timeInterval')}: {selectedSegment?.startDate} <SwapRightOutlined /> {selectedSegment.endDate}</div>
                        : <>
                          <div>{ln_t('target.activity')}: {selectedSegment?.interval} {ln_t('util.days')}</div>
                          <div>{ln_t('target.limit')}: {selectedSegment?.maxCount || ln_t('target.unlimited')}</div>
                          <div>{ln_t('segment.dataExport')}: {selectedSegment?.dataExport}</div>
                        </>}
                      <div style={{ paddingTop: '10px' }}>
                        <SegmentDetail detailOnly data={selectedSegment} />
                      </div>
                    </div>
                  </>
                : <Empty className="mt-5" description={<>
                    <div>{ln_t('target.noSelectedSegment')}</div>
                    <div>{ln_t('target.click')} <SelectOutlined style={{ padding: '0px 2px' }} /> {ln_t('target.toViewSegment')}</div>
                  </>} />}
            </CustomSettingWrapper>
          </SettingWrapper>
        </Form>

      </ConfigProvider>
      <TargetConfirmModal show={showModal} setShow={setShowModal} data={confirmData} goBack={() => navigate(goBackPath)} />
    </>
  )
}

export default TargetForm
